import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  CircularProgress,
  Container,
} from "@mui/material";
import { motion } from "framer-motion";
import { Person } from "@mui/icons-material";
import { FaUsers, FaCrown } from "react-icons/fa";
import { firestore } from "../Firebase";
import Bg from "../Images/hero.webp";

const Executives = () => {
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  useEffect(() => {
    const fetchExecutives = async () => {
      try {
        const snapshot = await firestore
          .collection("executives")
          .orderBy("order", "asc")
          .get();
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setExecutives(data);
      } catch (error) {
        console.error("Error fetching executives: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchExecutives();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
          backgroundSize: "cover",
        }}
      >
        <CircularProgress sx={{ color: "#00d9ff" }} size={60} />
        <Typography variant="h6" sx={{ color: "white", mt: 2 }}>
          Loading Executive Team...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 30px rgba(0, 217, 255, 0.4)",
                }}
              >
                <FaUsers size={40} color="white" />
              </Box>
            </motion.div>
          </Box>
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(90deg, #ffffff, #00d9ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Executive Team
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.7)",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Meet the leaders driving innovation and excellence at ICTAZ NIPA Chapter
          </Typography>
        </motion.div>

        {/* Executives Grid */}
        <Grid container spacing={4} justifyContent="center">
          {executives.length > 0 ? (
            executives.map((exec, index) => (
              <Grid item key={exec.id} xs={12} sm={6} md={4} lg={3}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <Card
                    sx={{
                      borderRadius: "25px",
                      ...darkGlassStyle,
                      transition: "all 0.3s ease",
                      overflow: "hidden",
                      "&:hover": {
                        border: "1px solid rgba(0, 217, 255, 0.5)",
                        boxShadow: "0 15px 40px rgba(0, 217, 255, 0.25)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: "center" }}>
                      {/* Image Container */}
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          mb: 2,
                        }}
                      >
                        {exec.imageUrl ? (
                          <CardMedia
                            component="img"
                            alt={exec.name}
                            image={exec.imageUrl}
                            sx={{
                              width: "100%",
                              height: 220,
                              borderRadius: "20px",
                              objectFit: "cover",
                              objectPosition: "top",
                              border: "3px solid rgba(0, 217, 255, 0.3)",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "linear-gradient(135deg, rgba(0, 217, 255, 0.1), rgba(0, 180, 216, 0.05))",
                              borderRadius: "20px",
                              border: "3px solid rgba(0, 217, 255, 0.3)",
                            }}
                          >
                            <Person sx={{ fontSize: 80, color: "rgba(0, 217, 255, 0.5)" }} />
                          </Box>
                        )}

                        {/* Position Badge for President */}
                        {exec.position?.toLowerCase().includes("president") && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #ffd700, #ffb700)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 4px 15px rgba(255, 215, 0, 0.5)",
                            }}
                          >
                            <FaCrown size={20} color="#000024" />
                          </Box>
                        )}
                      </Box>

                      {/* Name */}
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          color: "white",
                          mb: 0.5,
                        }}
                      >
                        {exec.name}
                      </Typography>

                      {/* Position */}
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 2,
                          py: 0.5,
                          borderRadius: "20px",
                          background: "linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(0, 180, 216, 0.1))",
                          border: "1px solid rgba(0, 217, 255, 0.3)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#00d9ff",
                            fontWeight: 600,
                          }}
                        >
                          {exec.position}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  sx={{
                    borderRadius: "25px",
                    ...darkGlassStyle,
                    p: 6,
                    textAlign: "center",
                  }}
                >
                  <FaUsers size={60} color="#00d9ff" style={{ marginBottom: 20 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: 'white' }}>
                    Executive Team Coming Soon
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    Our executive team members will be showcased here. Check back soon!
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default Executives;
